import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import CustomerFormModal from './CustomerFormModal';
import { toast } from './Toast';
import './ProjectFormModal.css';

function ProjectFormModal({ project, onClose, onSuccess }) {
  const { isCompanyAdmin, branchId: sessionBranchId } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    customerId: '',
    startDate: '',
    endDate: '',
    budget: '',
    projectManagerId: '',
    assignedEmployeeId: '',
    projectTypeId: '',
    description: '',
    status: 'PLANNED',
    branchId: ''
  });
  const [selectedManagerKey, setSelectedManagerKey] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const assigneeDropdownRef = useRef(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const employeeDropdownRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewTypeModal, setShowNewTypeModal] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newTypeForm, setNewTypeForm] = useState({
    name: '',
    description: '',
    active: true
  });

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        setEmployeeDropdownOpen(false);
      }
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target)) {
        setAssigneeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchEmployees();
    fetchProjectTypes();
    // Only fetch branches for COMPANY_ADMIN (branch managers use their own branch)
    if (isCompanyAdmin()) fetchBranches();
    
    if (project) {
      setFormData({
        name: project.name || '',
        customerId: project.customerId || '',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        budget: project.budget || '',
        projectManagerId: project.projectManagerId ? String(project.projectManagerId) : '',
        assignedEmployeeId: project.assignedEmployeeId ? String(project.assignedEmployeeId) : '',
        projectTypeId: project.projectTypeId || '',
        description: project.description || '',
        status: project.status || 'PLANNED',
        branchId: project.branchId || ''
      });
      let initAssignedIds = [];
      if (Array.isArray(project.assignedEmployeeIds) && project.assignedEmployeeIds.length > 0) {
        initAssignedIds = project.assignedEmployeeIds.map(id => Number(id));
      } else if (project.assignedEmployeeId) {
        initAssignedIds = [Number(project.assignedEmployeeId)];
      }
      setSelectedEmployeeIds(initAssignedIds);
    } else {
      setSelectedEmployeeIds([]);
      if (!isCompanyAdmin() && sessionBranchId) {
        // Pre-fill branch for non-admin users
        setFormData(prev => ({ ...prev, branchId: String(sessionBranchId) }));
      }
    }
  }, [project]);

  useEffect(() => {
    if (project && employees.length > 0) {
      let matched = null;
      if (project.assignedEmployeeId) {
        matched = employees.find(e => !e.isUserOnly && String(e.id) === String(project.assignedEmployeeId));
      }
      if (!matched && project.projectManagerId) {
        matched = employees.find(e => e.userId && String(e.userId) === String(project.projectManagerId));
      }
      if (matched) {
        setSelectedManagerKey(String(matched.id));
        setFormData(prev => ({
          ...prev,
          assignedEmployeeId: matched.isUserOnly ? '' : String(matched.id),
          projectManagerId: matched.userId ? String(matched.userId) : ''
        }));
      } else {
        setSelectedManagerKey('');
      }
    }
  }, [project, employees]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/customers?size=1000');
      const customerData = response.data.data?.content || response.data.content || [];
      setCustomers(customerData);
      return customerData;
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      return [];
    }
  };

  const getEmployeeRoleDisplay = (emp) => {
    if (emp.customRoleName && emp.customRoleName.trim()) {
      return emp.customRoleName.trim();
    }
    if (emp.userRole) {
      const roleMap = {
        'COMPANY_ADMIN': 'Company Admin',
        'BRANCH_MANAGER': 'Branch Manager',
        'SUPERVISOR': 'Site Supervisor',
        'EMPLOYEE': emp.designation || 'Employee',
        'SUPER_ADMIN': 'Super Admin'
      };
      if (roleMap[emp.userRole]) return roleMap[emp.userRole];
      return emp.userRole.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }
    if (emp.designation && emp.designation.trim()) {
      return emp.designation.trim();
    }
    return 'Employee';
  };

  const fetchEmployees = async () => {
    try {
      const [empRes, userRes] = await Promise.allSettled([
        axios.get('/employees?size=1000'),
        axios.get('/users?size=1000')
      ]);

      const empData = empRes.status === 'fulfilled'
        ? (empRes.value.data?.employees || empRes.value.data?.data?.employees || empRes.value.data?.data || empRes.value.data?.content || [])
        : [];
      const userData = userRes.status === 'fulfilled'
        ? (userRes.value.data?.content || userRes.value.data?.data?.content || userRes.value.data?.data || [])
        : [];

      const empList = Array.isArray(empData) ? [...empData] : [];
      const userList = Array.isArray(userData) ? userData : [];

      userList.forEach(u => {
        const alreadyInEmps = empList.some(e => e.userId && String(e.userId) === String(u.id));
        if (!alreadyInEmps && u.active !== false) {
          empList.push({
            id: `user-${u.id}`,
            userId: u.id,
            userName: u.name,
            userRole: u.role,
            customRoleName: u.customRoleName,
            isUserOnly: true
          });
        }
      });

      empList.sort((a, b) => {
        const nameA = (a.userName || a.name || '').toLowerCase();
        const nameB = (b.userName || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setEmployees(empList);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await axios.get('/project-types/active');
      const data = response.data.data || response.data;
      setProjectTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch project types:', err);
    }
  };

  const handleCreateProjectType = async () => {
    if (!newTypeForm.name.trim()) return;
    try {
      const response = await axios.post('/project-types', {
        name: newTypeForm.name.trim(),
        description: newTypeForm.description.trim(),
        active: newTypeForm.active
      });
      const data = response.data?.data || response.data;
      if (data && data.id) {
        await fetchProjectTypes();
        setFormData(prev => ({
          ...prev,
          projectTypeId: String(data.id)
        }));
      }
      setShowNewTypeModal(false);
      setNewTypeForm({ name: '', description: '', active: true });
    } catch (err) {
      console.error('Failed to create project type:', err);
      alert(err.response?.data?.message || 'Failed to create project type');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }

    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.budget || parseFloat(formData.budget) <= 0) {
      newErrors.budget = 'Budget must be a positive number';
    }

    if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return false;
    }
    return true;
  };

  const toggleEmployeeAccess = (empId) => {
    const idNum = Number(empId);
    setSelectedEmployeeIds(prev => 
      prev.includes(idNum) ? prev.filter(id => id !== idNum) : [...prev, idNum]
    );
  };

  const selectAllEmployees = () => {
    const validEmpIds = employees
      .filter(e => !e.isUserOnly && !isNaN(Number(e.id)))
      .map(e => Number(e.id));
    setSelectedEmployeeIds(validEmpIds);
  };

  const clearAllEmployees = () => {
    setSelectedEmployeeIds([]);
  };

  const selectedAssignee = employees.find(e => String(e.id) === String(selectedManagerKey));

  const handleSelectAssignee = (emp) => {
    if (!emp) {
      setSelectedManagerKey('');
      setFormData(prev => ({
        ...prev,
        projectManagerId: '',
        assignedEmployeeId: ''
      }));
      setAssigneeDropdownOpen(false);
      setAssigneeSearch('');
      return;
    }
    const key = String(emp.id);
    setSelectedManagerKey(key);
    setFormData(prev => ({
      ...prev,
      assignedEmployeeId: emp.isUserOnly ? '' : String(emp.id),
      projectManagerId: emp.userId ? String(emp.userId) : ''
    }));
    if (!emp.isUserOnly && !isNaN(Number(emp.id))) {
      const empIdNum = Number(emp.id);
      setSelectedEmployeeIds(prev => prev.includes(empIdNum) ? prev : [...prev, empIdNum]);
    }
    setAssigneeDropdownOpen(false);
    setAssigneeSearch('');
  };

  const handleClearAssignee = (e) => {
    e.stopPropagation();
    handleSelectAssignee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const selectedEmp = employees.find(e => String(e.id) === String(selectedManagerKey));
      const assignedEmpId = selectedEmp && !selectedEmp.isUserOnly ? parseInt(selectedEmp.id) : null;
      const pmUserId = selectedEmp?.userId ? parseInt(selectedEmp.userId) : null;

      let finalAssignedIds = [...selectedEmployeeIds];
      if (assignedEmpId && !finalAssignedIds.includes(assignedEmpId)) {
        finalAssignedIds.push(assignedEmpId);
      }

      const payload = {
        ...formData,
        customerId: parseInt(formData.customerId),
        assignedEmployeeId: assignedEmpId,
        assignedEmployeeIds: finalAssignedIds,
        projectManagerId: pmUserId,
        branchId: formData.branchId ? parseInt(formData.branchId) : null,
        budget: parseFloat(formData.budget)
      };

      if (project) {
        await axios.put(`/projects/${project.id}`, payload);
      } else {
        await axios.post('/projects', payload);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{project ? 'Edit Project' : 'Create Project'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Project Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter project name"
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group" style={{ position: 'relative' }}>
            <label htmlFor="customerId">Customer *</label>
            <input
              type="text"
              id="customerId"
              placeholder="Search Customer..."
              value={customerDropdownOpen ? customerSearch : (customers.find(c => String(c.id) === String(formData.customerId))?.name || '')}
              onFocus={() => {
                setCustomerDropdownOpen(true);
                setCustomerSearch('');
              }}
              onBlur={() => setTimeout(() => setCustomerDropdownOpen(false), 250)}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className={errors.customerId ? 'error' : ''}
              autoComplete="off"
              required
            />
            {customerDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 100,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <div
                  onMouseDown={() => {
                    setShowNewCustomerModal(true);
                    setCustomerDropdownOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    fontWeight: '500',
                    textAlign: 'center'
                  }}
                >
                  + Create New Customer
                </div>
                {customers
                  .filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
                  .map(c => (
                    <div
                      key={c.id}
                      onMouseDown={() => {
                        setFormData(prev => ({ ...prev, customerId: String(c.id) }));
                        setCustomerDropdownOpen(false);
                        setCustomerSearch('');
                        if (errors.customerId) {
                          setErrors(prev => ({ ...prev, customerId: '' }));
                        }
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                    >
                      {c.name}
                    </div>
                  ))}
                {customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                  <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>No customers found</div>
                )}
              </div>
            )}
            {errors.customerId && <span className="error-message">{errors.customerId}</span>}
          </div>

          {isCompanyAdmin() && (
            <div className="form-group">
              <label htmlFor="branchId">Branch</label>
              <select
                id="branchId"
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
              >
                <option value="">Select Branch (optional)</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="projectTypeId">Project Type</label>
            <select
              id="projectTypeId"
              name="projectTypeId"
              value={formData.projectTypeId}
              onChange={(e) => {
                if (e.target.value === 'ADD_NEW') {
                  setShowNewTypeModal(true);
                  setFormData(prev => ({ ...prev, projectTypeId: '' }));
                } else {
                  handleChange(e);
                }
              }}
            >
              <option value="">Select Type (optional)</option>
              {projectTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
              <option value="ADD_NEW">+ Add New Project Type</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Start Date *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className={errors.startDate ? 'error' : ''}
                required
              />
              {errors.startDate && <span className="error-message">{errors.startDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className={errors.endDate ? 'error' : ''}
              />
              {errors.endDate && <span className="error-message">{errors.endDate}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="budget">Budget (₹) *</label>
              <input
                type="number"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className={errors.budget ? 'error' : ''}
                placeholder="Enter budget"
                step="0.01"
                required
              />
              {errors.budget && <span className="error-message">{errors.budget}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="PLANNED">Planned</option>
                <option value="ACTIVE">Active</option>
                <option value="DELAYED">Delayed</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="form-group searchable-select-container" ref={assigneeDropdownRef}>
            <label htmlFor="projectAssignee">Project Assignee</label>
            <div className="searchable-select-input-wrap">
              <input
                type="text"
                id="projectAssignee"
                placeholder="Search & select Project Assignee..."
                value={
                  assigneeDropdownOpen
                    ? assigneeSearch
                    : (selectedAssignee
                        ? `${selectedAssignee.userName || selectedAssignee.name || 'Unnamed'} (${getEmployeeRoleDisplay(selectedAssignee)})${selectedAssignee.employeeCode ? ` - ${selectedAssignee.employeeCode}` : ''}`
                        : '')
                }
                onFocus={() => {
                  setAssigneeDropdownOpen(true);
                  setAssigneeSearch('');
                }}
                onClick={() => {
                  if (!assigneeDropdownOpen) {
                    setAssigneeDropdownOpen(true);
                    setAssigneeSearch('');
                  }
                }}
                onChange={(e) => {
                  setAssigneeSearch(e.target.value);
                  if (!assigneeDropdownOpen) setAssigneeDropdownOpen(true);
                }}
                className="searchable-select-input"
                autoComplete="off"
              />
              <div className="searchable-select-controls">
                {selectedManagerKey && (
                  <button
                    type="button"
                    className="searchable-select-clear-btn"
                    onClick={handleClearAssignee}
                    title="Clear selection"
                  >
                    &times;
                  </button>
                )}
                <button
                  type="button"
                  className="searchable-select-arrow-btn"
                  onClick={() => {
                    setAssigneeDropdownOpen(prev => !prev);
                    if (!assigneeDropdownOpen) setAssigneeSearch('');
                  }}
                  tabIndex={-1}
                >
                  {assigneeDropdownOpen ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {assigneeDropdownOpen && (
              <div className="searchable-select-dropdown">
                <div
                  className={`searchable-select-option unassigned ${!selectedManagerKey ? 'selected' : ''}`}
                  onClick={() => handleSelectAssignee(null)}
                >
                  <div className="emp-avatar-circle" style={{ background: '#9ca3af', fontSize: '11px' }}>
                    —
                  </div>
                  <div className="emp-info-col">
                    <span className="emp-name" style={{ color: '#4b5563' }}>None / Unassigned</span>
                  </div>
                  {!selectedManagerKey && <span className="searchable-select-check">✓</span>}
                </div>

                {employees
                  .filter(emp => {
                    if (!assigneeSearch.trim()) return true;
                    const term = assigneeSearch.toLowerCase();
                    const name = (emp.userName || emp.name || '').toLowerCase();
                    const code = (emp.employeeCode || '').toLowerCase();
                    const role = getEmployeeRoleDisplay(emp).toLowerCase();
                    return name.includes(term) || code.includes(term) || role.includes(term);
                  })
                  .map(emp => {
                    const isSelected = String(emp.id) === String(selectedManagerKey);
                    const empName = emp.userName || emp.name || 'Unnamed';
                    const roleText = getEmployeeRoleDisplay(emp);
                    return (
                      <div
                        key={emp.id}
                        className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectAssignee(emp)}
                      >
                        <div className="emp-avatar-circle">
                          {empName.charAt(0).toUpperCase()}
                        </div>
                        <div className="emp-info-col">
                          <div className="emp-name-line">
                            <span className="emp-name">{empName}</span>
                            {emp.employeeCode && (
                              <span className="emp-code-badge">{emp.employeeCode}</span>
                            )}
                          </div>
                          <span className="emp-role-subtitle">{roleText}</span>
                        </div>
                        {isSelected && <span className="searchable-select-check">✓</span>}
                      </div>
                    );
                  })}

                {employees.filter(emp => {
                  if (!assigneeSearch.trim()) return true;
                  const term = assigneeSearch.toLowerCase();
                  const name = (emp.userName || emp.name || '').toLowerCase();
                  const code = (emp.employeeCode || '').toLowerCase();
                  const role = getEmployeeRoleDisplay(emp).toLowerCase();
                  return name.includes(term) || code.includes(term) || role.includes(term);
                }).length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    No assignees found
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group" ref={employeeDropdownRef} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>
                Who Can Access This Project (Assigned Employees)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {selectedEmployeeIds.length} employee{selectedEmployeeIds.length === 1 ? '' : 's'} selected
                </span>
                {selectedEmployeeIds.length > 0 && (
                  <button
                    type="button"
                    className="dropdown-action-btn cancel-all-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      clearAllEmployees();
                    }}
                    style={{ fontSize: '12px', padding: '1px 5px', cursor: 'pointer' }}
                  >
                    Cancel All
                  </button>
                )}
              </div>
            </div>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#6b7280' }}>
              Select multiple employees who can see or access this project to submit timesheets or supervisor updates.
            </p>

            {/* Selected chips container */}
            <div
              className="employee-chips-container"
              onClick={() => setEmployeeDropdownOpen(true)}
              style={{ cursor: 'pointer' }}
            >
              {selectedEmployeeIds.length === 0 ? (
                <span className="no-selection-placeholder">No employees selected (click to choose)</span>
              ) : (
                selectedEmployeeIds.map(empId => {
                  const emp = employees.find(e => !e.isUserOnly && Number(e.id) === Number(empId));
                  const name = emp ? (emp.userName || emp.name || `Employee #${empId}`) : `Employee #${empId}`;
                  const code = emp?.employeeCode ? ` (${emp.employeeCode})` : '';
                  return (
                    <span key={empId} className="employee-chip">
                      <span className="chip-name">{name}{code}</span>
                      <button
                        type="button"
                        className="chip-remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEmployeeAccess(empId);
                        }}
                        title="Remove"
                      >
                        &times;
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            {/* Search input / dropdown trigger */}
            <div style={{ position: 'relative', marginTop: '6px' }}>
              <input
                type="text"
                placeholder="Search employees to grant access..."
                value={employeeSearch}
                onFocus={() => setEmployeeDropdownOpen(true)}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  if (!employeeDropdownOpen) setEmployeeDropdownOpen(true);
                }}
                autoComplete="off"
              />
              <button
                type="button"
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#6b7280'
                }}
                onClick={() => setEmployeeDropdownOpen(prev => !prev)}
              >
                {employeeDropdownOpen ? '▲' : '▼'}
              </button>
            </div>

            {/* Dropdown menu */}
            {employeeDropdownOpen && (
              <div className="employee-multiselect-dropdown">
                <div className="dropdown-actions-bar">
                  <button
                    type="button"
                    className="dropdown-action-btn select-all-btn"
                    onClick={selectAllEmployees}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="dropdown-action-btn cancel-all-btn"
                    onClick={clearAllEmployees}
                  >
                    Cancel All
                  </button>
                </div>

                <div className="dropdown-items-scroll">
                  {employees
                    .filter(emp => !emp.isUserOnly)
                    .filter(emp => {
                      if (!employeeSearch.trim()) return true;
                      const term = employeeSearch.toLowerCase();
                      const name = (emp.userName || emp.name || '').toLowerCase();
                      const code = (emp.employeeCode || '').toLowerCase();
                      const desig = (emp.designation || '').toLowerCase();
                      return name.includes(term) || code.includes(term) || desig.includes(term);
                    })
                    .map(emp => {
                      const isSelected = selectedEmployeeIds.includes(Number(emp.id));
                      const empName = emp.userName || emp.name || 'Unnamed';
                      const roleText = getEmployeeRoleDisplay(emp);
                      return (
                        <div
                          key={emp.id}
                          className={`dropdown-employee-row ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleEmployeeAccess(emp.id)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            style={{ width: '16px', height: '16px', cursor: 'pointer', margin: '0 10px 0 0' }}
                          />
                          <div className="emp-avatar-circle">
                            {empName.charAt(0).toUpperCase()}
                          </div>
                          <div className="emp-info-col">
                            <div className="emp-name-line">
                              <span className="emp-name">{empName}</span>
                              {emp.employeeCode && (
                                <span className="emp-code-badge">{emp.employeeCode}</span>
                              )}
                            </div>
                            <span className="emp-role-subtitle">{roleText}</span>
                          </div>
                        </div>
                      );
                    })}
                  {employees.filter(emp => !emp.isUserOnly).length === 0 && (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      No employees available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Enter project description"
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (project ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>

      {showNewTypeModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewTypeModal(false);
            setNewTypeForm({ name: '', description: '', active: true });
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '450px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>New Project Type</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewTypeModal(false);
                  setNewTypeForm({ name: '', description: '', active: true });
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Name *</label>
                <input
                  type="text"
                  value={newTypeForm.name}
                  onChange={(e) => setNewTypeForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Residential"
                  required
                />
              </div>
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newTypeForm.description}
                  onChange={(e) => setNewTypeForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe this project type..."
                  rows={3}
                />
              </div>
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newTypeForm.active}
                    onChange={(e) => setNewTypeForm(p => ({ ...p, active: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border, #e5e7eb)', background: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewTypeModal(false);
                  setNewTypeForm({ name: '', description: '', active: true });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateProjectType}
                disabled={!newTypeForm.name.trim()}
              >
                Save Project Type
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewCustomerModal && (
        <CustomerFormModal
          onClose={() => setShowNewCustomerModal(false)}
          onSuccess={async () => {
            setShowNewCustomerModal(false);
            const updatedCustomers = await fetchCustomers();
            if (updatedCustomers && updatedCustomers.length > 0) {
              const maxIdCustomer = updatedCustomers.reduce((max, c) => c.id > max.id ? c : max, updatedCustomers[0]);
              if (maxIdCustomer) {
                setFormData(prev => ({ ...prev, customerId: String(maxIdCustomer.id) }));
                if (errors.customerId) {
                  setErrors(prev => ({ ...prev, customerId: '' }));
                }
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectFormModal;
